// Types for payment status
export type PaymentStatus = 'healthy' | 'in-retry' | 'on-hold' | 'failed'
export interface PaymentInfo {
  status: PaymentStatus
  retryCount?: number // Current retry attempt (1-4)
  maxRetries?: number // Maximum retries (typically 4)
  nextRetry?: string // ISO date string for next retry attempt
  failureReason?: string // Reason for payment failure
}

// Card expiry information
export interface CardExpiryInfo {
  expiryMonth: number // 1-12
  expiryYear: number // YYYY
  last4: string // Last 4 digits of card
  brand: string // Visa, Mastercard, Amex, etc.
}

// Mock subscriptions data - synced with SubscriptionTable.tsx
export const subscriptions = [
  {
    id: 'SUB-23144',
    status: 'Active',
    customerName: 'Sarah Johnson',
    customer: 'testdemo9@winery.com',
    location: 'Melbourne, VIC',
    amount: '$126.50',
    subscriptionTitle: '3 Bottles Every Month',
    discountPercent: 10,
    nextPayment: 'Wed, 11 Mar 2026',
    startDate: 'Mon, 17 Mar 2025',
    isSkipped: true,
    orderCount: 18,
    tags: ['premium', 'vip'],
    paymentInfo: {
      status: 'healthy' as PaymentStatus,
    },
    cardExpiry: {
      expiryMonth: 5,
      expiryYear: 2026,
      // Expires in ~2 months (May 2026)
      last4: '4242',
      brand: 'Visa',
    },
    items: [
      {
        quantity: 1,
        name: '25FID6 – 2025 Fiano',
      },
      {
        quantity: 1,
        name: '24SHZ9 – 2024 Shiraz',
      },
      {
        quantity: 1,
        name: '25CHD3 – 2025 Chardonnay',
      },
    ],
  },
  {
    id: 'SUB-23141',
    status: 'Active',
    customerName: 'Michael Chen',
    customer: 'demov18@winery.com',
    location: 'Yarraville, VIC',
    amount: '$189.50',
    subscriptionTitle: '3 Bottles Every 3 Months',
    discountPercent: 5,
    nextPayment: 'Fri, 13 Mar 2026',
    startDate: 'Mon, 17 Mar 2025',
    orderCount: 7,
    tags: ['new-customer'],
    paymentInfo: {
      status: 'in-retry' as PaymentStatus,
      retryCount: 2,
      maxRetries: 4,
      nextRetry: new Date(Date.now() + 18 * 60 * 60 * 1000).toISOString(),
      // Tomorrow 9:00 AM
      failureReason: 'Insufficient funds',
    },
    cardExpiry: {
      expiryMonth: 4,
      expiryYear: 2026,
      // Expires in ~1 month (Apr 2026) - URGENT
      last4: '5678',
      brand: 'Mastercard',
    },
    items: [
      {
        quantity: 1,
        name: '23PNR7 – 2023 Pinot Noir',
      },
      {
        quantity: 1,
        name: '24SBB2 – 2024 Sauvignon Blanc',
      },
      {
        quantity: 1,
        name: '22CSR8 – 2022 Cabernet Sauvignon Reserve',
      },
    ],
  },
  {
    id: 'SUB-23139',
    status: 'Paused',
    customerName: 'Emma Williams',
    customer: 'demov211@winery.com',
    location: 'Adelaide, SA',
    amount: '$245.00',
    subscriptionTitle: '6 Bottles Every 3 Months',
    discountPercent: 10,
    nextPayment: 'Mon, 16 Mar 2026',
    startDate: 'Mon, 17 Mar 2025',
    orderCount: 12,
    tags: ['seasonal'],
    paymentInfo: {
      status: 'healthy' as PaymentStatus,
    },
    cardExpiry: {
      expiryMonth: 8,
      expiryYear: 2026,
      // Expires in 8 months
      last4: '9012',
      brand: 'Visa',
    },
    items: [
      {
        quantity: 2,
        name: '25FID6 – 2025 Fiano',
      },
      {
        quantity: 2,
        name: '25RSG5 – 2025 Rosé Grenache',
      },
      {
        quantity: 2,
        name: '23TEM4 – 2023 Tempranillo',
      },
    ],
  },
  {
    id: 'SUB-23138',
    status: 'At Risk',
    customerName: 'James Anderson',
    customer: 'james.a@winery.com',
    location: 'Sydney, NSW',
    amount: '$315.00',
    subscriptionTitle: '6 Bottles Every 6 Months',
    discountPercent: 5,
    nextPayment: 'Thu, 19 Mar 2026',
    startDate: 'Tue, 18 Mar 2025',
    orderCount: 4,
    tags: ['needs-attention', 'high-value'],
    paymentInfo: {
      status: 'in-retry' as PaymentStatus,
      retryCount: 4,
      maxRetries: 4,
      nextRetry: new Date(Date.now() + 3 * 60 * 60 * 1000).toISOString(),
      // In 3 hours
      failureReason: 'Card declined',
    },
    cardExpiry: {
      expiryMonth: 4,
      expiryYear: 2026,
      // Expires in ~1 month (Apr 2026) - URGENT
      last4: '3456',
      brand: 'Amex',
    },
    items: [
      {
        quantity: 2,
        name: '23PNR7 – 2023 Pinot Noir',
      },
      {
        quantity: 2,
        name: '24PGR1 – 2024 Pinot Grigio',
      },
      {
        quantity: 2,
        name: '22CSR8 – 2022 Cabernet Sauvignon Reserve',
      },
    ],
  },
  {
    id: 'SUB-23137',
    status: 'Active',
    customerName: 'Olivia Martinez',
    customer: 'olivia.m@winery.com',
    location: 'Brisbane, QLD',
    amount: '$98.75',
    subscriptionTitle: '12 Bottles Every 3 Months',
    discountPercent: 20,
    nextPayment: 'Sat, 22 Mar 2026',
    startDate: 'Wed, 19 Mar 2025',
    isSkipped: true,
    orderCount: 22,
    tags: ['loyal', 'vip'],
    paymentInfo: {
      status: 'on-hold' as PaymentStatus,
      retryCount: 4,
      maxRetries: 4,
      failureReason: 'Max retries exhausted',
    },
    cardExpiry: {
      expiryMonth: 5,
      expiryYear: 2026,
      // Expires in ~2 months
      last4: '7890',
      brand: 'Visa',
    },
    items: [
      {
        quantity: 3,
        name: '25FID6 – 2025 Fiano',
      },
      {
        quantity: 3,
        name: '24SHZ9 – 2024 Shiraz',
      },
      {
        quantity: 3,
        name: '25CHD3 – 2025 Chardonnay',
      },
      {
        quantity: 3,
        name: '22MLB6 – 2022 Malbec',
      },
    ],
  },
  {
    id: 'SUB-23136',
    status: 'On Hold',
    customerName: 'William Taylor',
    customer: 'w.taylor@winery.com',
    location: 'Perth, WA',
    amount: '$275.50',
    subscriptionTitle: '12 Bottles Every 6 Months',
    discountPercent: 10,
    nextPayment: 'Tue, 25 Mar 2026',
    startDate: 'Thu, 20 Mar 2025',
    orderCount: 9,
    tags: ['corporate'],
    paymentInfo: {
      status: 'failed' as PaymentStatus,
      failureReason: 'Payment method revoked',
    },
    cardExpiry: {
      expiryMonth: 4,
      expiryYear: 2026,
      // Expiring soon (Apr 2026)
      last4: '1234',
      brand: 'Visa',
    },
    items: [
      {
        quantity: 3,
        name: '22CSR8 – 2022 Cabernet Sauvignon Reserve',
      },
      {
        quantity: 3,
        name: '24SHZ9 – 2024 Shiraz',
      },
      {
        quantity: 3,
        name: '23PNR7 – 2023 Pinot Noir',
      },
      {
        quantity: 3,
        name: '25VGN9 – 2025 Viognier',
      },
    ],
  },
  {
    id: 'SUB-23135',
    status: 'Active',
    customerName: 'Sophia Brown',
    customer: 'sophia.b@winery.com',
    location: 'Hobart, TAS',
    amount: '$142.00',
    subscriptionTitle: '3 Bottles Every Month',
    discountPercent: 10,
    nextPayment: 'Fri, 28 Mar 2026',
    startDate: 'Fri, 21 Mar 2025',
    isSkipped: true,
    orderCount: 15,
    tags: ['premium'],
    paymentInfo: {
      status: 'healthy' as PaymentStatus,
    },
    cardExpiry: {
      expiryMonth: 4,
      expiryYear: 2026,
      // Expires in ~1 month (Apr 2026) - URGENT
      last4: '5555',
      brand: 'Mastercard',
    },
    items: [
      {
        quantity: 1,
        name: '25RSG5 – 2025 Rosé Grenache',
      },
      {
        quantity: 1,
        name: '24SBB2 – 2024 Sauvignon Blanc',
      },
      {
        quantity: 1,
        name: '25CHD3 – 2025 Chardonnay',
      },
    ],
  },
  {
    id: 'SUB-23134',
    status: 'Cancelled',
    customerName: 'Liam Wilson',
    customer: 'liam.w@winery.com',
    location: 'Canberra, ACT',
    amount: '$365.00',
    subscriptionTitle: '3 Bottles Every 3 Months',
    discountPercent: 5,
    nextPayment: 'Mon, 31 Mar 2026',
    startDate: 'Sat, 22 Mar 2025',
    endDate: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
    orderCount: 3,
    tags: [],
    paymentInfo: {
      status: 'healthy' as PaymentStatus,
    },
    cardExpiry: {
      expiryMonth: 9,
      expiryYear: 2026,
      last4: '6666',
      brand: 'Visa',
    },
    items: [
      {
        quantity: 1,
        name: '22CSR8 – 2022 Cabernet Sauvignon Reserve',
      },
      {
        quantity: 1,
        name: '22MLB6 – 2022 Malbec',
      },
      {
        quantity: 1,
        name: '23GSM3 – 2023 GSM Blend',
      },
    ],
  },
  {
    id: 'SUB-23133',
    status: 'Active',
    customerName: 'Isabella Garcia',
    customer: 'isabella.g@winery.com',
    location: 'Gold Coast, QLD',
    amount: '$156.25',
    subscriptionTitle: '6 Bottles Every 3 Months',
    discountPercent: 10,
    nextPayment: 'Thu, 3 Apr 2026',
    startDate: 'Sun, 23 Mar 2025',
    isSkipped: true,
    skipCount: 2,
    orderCount: 20,
    tags: ['loyal'],
    paymentInfo: {
      status: 'in-retry' as PaymentStatus,
      retryCount: 1,
      maxRetries: 4,
      nextRetry: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
      // 5 days from now
      failureReason: 'Card expired',
    },
    cardExpiry: {
      expiryMonth: 4,
      expiryYear: 2026,
      // Expiring soon (Apr 2026)
      last4: '7777',
      brand: 'Visa',
    },
    items: [
      {
        quantity: 2,
        name: '25FID6 – 2025 Fiano',
      },
      {
        quantity: 2,
        name: '25VGN9 – 2025 Viognier',
      },
      {
        quantity: 2,
        name: '23TEM4 – 2023 Tempranillo',
      },
    ],
  },
  {
    id: 'SUB-23132',
    status: 'Active',
    customerName: 'Noah Rodriguez',
    customer: 'noah.r@winery.com',
    location: 'Newcastle, NSW',
    amount: '$210.50',
    subscriptionTitle: '6 Bottles Every 6 Months',
    discountPercent: 5,
    nextPayment: 'Sun, 5 Apr 2026',
    startDate: 'Mon, 24 Mar 2025',
    orderCount: 11,
    tags: ['new-customer'],
    paymentInfo: {
      status: 'healthy' as PaymentStatus,
    },
    cardExpiry: {
      expiryMonth: 5,
      expiryYear: 2026,
      // Expires in ~2 months
      last4: '8888',
      brand: 'Mastercard',
    },
    items: [
      {
        quantity: 2,
        name: '23PNR7 – 2023 Pinot Noir',
      },
      {
        quantity: 2,
        name: '24SHZ9 – 2024 Shiraz',
      },
      {
        quantity: 2,
        name: '22CSR8 – 2022 Cabernet Sauvignon Reserve',
      },
    ],
  },
  {
    id: 'SUB-23131',
    status: 'Paused',
    customerName: 'Ava Lee',
    customer: 'ava.lee@winery.com',
    location: 'Geelong, VIC',
    amount: '$185.00',
    subscriptionTitle: '12 Bottles Every 3 Months',
    discountPercent: 20,
    nextPayment: 'Wed, 8 Apr 2026',
    startDate: 'Tue, 25 Mar 2025',
    orderCount: 25,
    tags: ['loyal', 'vip'],
    paymentInfo: {
      status: 'healthy' as PaymentStatus,
    },
    cardExpiry: {
      expiryMonth: 6,
      expiryYear: 2026,
      last4: '9999',
      brand: 'Visa',
    },
    items: [
      {
        quantity: 3,
        name: '25FID6 – 2025 Fiano',
      },
      {
        quantity: 3,
        name: '25CHD3 – 2025 Chardonnay',
      },
      {
        quantity: 3,
        name: '24PGR1 – 2024 Pinot Grigio',
      },
      {
        quantity: 3,
        name: '25RSG5 – 2025 Rosé Grenache',
      },
    ],
  },
  {
    id: 'SUB-23130',
    status: 'At Risk',
    customerName: 'Ethan White',
    customer: 'ethan.w@winery.com',
    location: 'Wollongong, NSW',
    amount: '$298.75',
    subscriptionTitle: '12 Bottles Every 6 Months',
    discountPercent: 10,
    nextPayment: 'Sat, 11 Apr 2026',
    startDate: 'Wed, 26 Mar 2025',
    orderCount: 6,
    tags: ['needs-attention', 'high-value'],
    paymentInfo: {
      status: 'in-retry' as PaymentStatus,
      retryCount: 3,
      maxRetries: 4,
      nextRetry: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      // Overdue (2 hours ago)
      failureReason: 'Bank declined',
    },
    cardExpiry: {
      expiryMonth: 4,
      expiryYear: 2026,
      // Expires in ~1 month
      last4: '1111',
      brand: 'Visa',
    },
    items: [
      {
        quantity: 3,
        name: '22CSR8 – 2022 Cabernet Sauvignon Reserve',
      },
      {
        quantity: 3,
        name: '24SHZ9 – 2024 Shiraz',
      },
      {
        quantity: 3,
        name: '23PNR7 – 2023 Pinot Noir',
      },
      {
        quantity: 3,
        name: '22MLB6 – 2022 Malbec',
      },
    ],
  },
  {
    id: 'SUB-23129',
    status: 'Active',
    customerName: 'Mia Harris',
    customer: 'mia.h@winery.com',
    location: 'Cairns, QLD',
    amount: '$115.00',
    subscriptionTitle: '3 Bottles Every Month',
    discountPercent: 10,
    nextPayment: 'Tue, 14 Apr 2026',
    startDate: 'Thu, 27 Mar 2025',
    isSkipped: true,
    orderCount: 14,
    tags: ['seasonal'],
    paymentInfo: {
      status: 'healthy' as PaymentStatus,
    },
    cardExpiry: {
      expiryMonth: 4,
      expiryYear: 2026,
      // Expires in ~1 month - URGENT
      last4: '2222',
      brand: 'Mastercard',
    },
    items: [
      {
        quantity: 1,
        name: '24SBB2 – 2024 Sauvignon Blanc',
      },
      {
        quantity: 1,
        name: '25CHD3 – 2025 Chardonnay',
      },
      {
        quantity: 1,
        name: '24PGR1 – 2024 Pinot Grigio',
      },
    ],
  },
  {
    id: 'SUB-23128',
    status: 'Active',
    customerName: 'Lucas Martin',
    customer: 'lucas.m@winery.com',
    location: 'Townsville, QLD',
    amount: '$225.50',
    subscriptionTitle: '3 Bottles Every 3 Months',
    discountPercent: 5,
    nextPayment: 'Fri, 17 Apr 2026',
    startDate: 'Fri, 28 Mar 2025',
    orderCount: 8,
    tags: [],
    paymentInfo: {
      status: 'healthy' as PaymentStatus,
    },
    cardExpiry: {
      expiryMonth: 10,
      expiryYear: 2026,
      last4: '3333',
      brand: 'Visa',
    },
    items: [
      {
        quantity: 1,
        name: '23PNR7 – 2023 Pinot Noir',
      },
      {
        quantity: 1,
        name: '22MLB6 – 2022 Malbec',
      },
      {
        quantity: 1,
        name: '23GSM3 – 2023 GSM Blend',
      },
    ],
  },
  {
    id: 'SUB-23127',
    status: 'On Hold',
    customerName: 'Charlotte Thompson',
    customer: 'charlotte.t@winery.com',
    location: 'Darwin, NT',
    amount: '$335.25',
    subscriptionTitle: '6 Bottles Every 3 Months',
    discountPercent: 10,
    nextPayment: 'Mon, 20 Apr 2026',
    startDate: 'Sat, 29 Mar 2025',
    orderCount: 13,
    tags: ['corporate', 'high-value'],
    paymentInfo: {
      status: 'on-hold' as PaymentStatus,
      retryCount: 4,
      maxRetries: 4,
      failureReason: 'Awaiting customer action',
    },
    cardExpiry: {
      expiryMonth: 5,
      expiryYear: 2026,
      // Expires in ~2 months
      last4: '4444',
      brand: 'Amex',
    },
    items: [
      {
        quantity: 2,
        name: '22CSR8 – 2022 Cabernet Sauvignon Reserve',
      },
      {
        quantity: 2,
        name: '24SHZ9 – 2024 Shiraz',
      },
      {
        quantity: 2,
        name: '23TEM4 – 2023 Tempranillo',
      },
    ],
  },
  {
    id: 'SUB-23126',
    status: 'Cancelled',
    customerName: 'Mason Jackson',
    customer: 'mason.j@winery.com',
    location: 'Ballarat, VIC',
    amount: '$380.00',
    subscriptionTitle: '6 Bottles Every 6 Months',
    discountPercent: 5,
    nextPayment: 'Thu, 23 Apr 2026',
    startDate: 'Sun, 30 Mar 2025',
    endDate: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
    orderCount: 5,
    tags: [],
    paymentInfo: {
      status: 'healthy' as PaymentStatus,
    },
    cardExpiry: {
      expiryMonth: 7,
      expiryYear: 2026,
      last4: '5555',
      brand: 'Visa',
    },
    items: [
      {
        quantity: 2,
        name: '23PNR7 – 2023 Pinot Noir',
      },
      {
        quantity: 2,
        name: '22CSR8 – 2022 Cabernet Sauvignon Reserve',
      },
      {
        quantity: 2,
        name: '24SHZ9 – 2024 Shiraz',
      },
    ],
  },
  {
    id: 'SUB-23125',
    status: 'Active',
    customerName: 'Amelia Clark',
    customer: 'amelia.c@winery.com',
    location: 'Bendigo, VIC',
    amount: '$89.75',
    subscriptionTitle: '12 Bottles Every 3 Months',
    discountPercent: 20,
    nextPayment: 'Sun, 26 Apr 2026',
    startDate: 'Mon, 31 Mar 2025',
    orderCount: 19,
    tags: ['loyal', 'premium'],
    paymentInfo: {
      status: 'healthy' as PaymentStatus,
    },
    cardExpiry: {
      expiryMonth: 4,
      expiryYear: 2026,
      // Expires in ~1 month - URGENT
      last4: '6666',
      brand: 'Mastercard',
    },
    items: [
      {
        quantity: 3,
        name: '25FID6 – 2025 Fiano',
      },
      {
        quantity: 3,
        name: '25CHD3 – 2025 Chardonnay',
      },
      {
        quantity: 3,
        name: '25VGN9 – 2025 Viognier',
      },
      {
        quantity: 3,
        name: '24SBB2 – 2024 Sauvignon Blanc',
      },
    ],
  },
  {
    id: 'SUB-23124',
    status: 'Active',
    customerName: 'Benjamin Lewis',
    customer: 'benjamin.l@winery.com',
    location: 'Launceston, TAS',
    amount: '$167.50',
    subscriptionTitle: '12 Bottles Every 6 Months',
    discountPercent: 10,
    nextPayment: 'Wed, 29 Apr 2026',
    startDate: 'Tue, 1 Apr 2025',
    orderCount: 10,
    tags: ['new-customer'],
    paymentInfo: {
      status: 'healthy' as PaymentStatus,
    },
    cardExpiry: {
      expiryMonth: 11,
      expiryYear: 2026,
      last4: '7777',
      brand: 'Visa',
    },
    items: [
      {
        quantity: 3,
        name: '22CSR8 – 2022 Cabernet Sauvignon Reserve',
      },
      {
        quantity: 3,
        name: '24SHZ9 – 2024 Shiraz',
      },
      {
        quantity: 3,
        name: '23PNR7 – 2023 Pinot Noir',
      },
      {
        quantity: 3,
        name: '23GSM3 – 2023 GSM Blend',
      },
    ],
  },
  {
    id: 'SUB-23123',
    status: 'Paused',
    customerName: 'Harper Walker',
    customer: 'harper.w@winery.com',
    location: 'Toowoomba, QLD',
    amount: '$252.00',
    subscriptionTitle: '3 Bottles Every Month',
    discountPercent: 10,
    nextPayment: 'Sat, 2 May 2026',
    startDate: 'Wed, 2 Apr 2025',
    orderCount: 16,
    tags: ['premium'],
    paymentInfo: {
      status: 'healthy' as PaymentStatus,
    },
    cardExpiry: {
      expiryMonth: 5,
      expiryYear: 2026,
      last4: '8888',
      brand: 'Mastercard',
    },
    items: [
      {
        quantity: 1,
        name: '25RSG5 – 2025 Rosé Grenache',
      },
      {
        quantity: 1,
        name: '25CHD3 – 2025 Chardonnay',
      },
      {
        quantity: 1,
        name: '24PGR1 – 2024 Pinot Grigio',
      },
    ],
  },
  {
    id: 'SUB-23122',
    status: 'Active',
    customerName: 'Alexander Hall',
    customer: 'alex.h@winery.com',
    location: 'Albury, NSW',
    amount: '$75.25',
    subscriptionTitle: '3 Bottles Every 3 Months',
    discountPercent: 5,
    nextPayment: 'Tue, 12 May 2026',
    startDate: 'Thu, 3 Apr 2025',
    orderCount: 1,
    tags: ['new-customer'],
    paymentInfo: {
      status: 'healthy' as PaymentStatus,
    },
    cardExpiry: {
      expiryMonth: 12,
      expiryYear: 2026,
      last4: '9999',
      brand: 'Visa',
    },
    items: [
      {
        quantity: 1,
        name: '22MLB6 – 2022 Malbec',
      },
      {
        quantity: 1,
        name: '22CSR8 – 2022 Cabernet Sauvignon Reserve',
      },
      {
        quantity: 1,
        name: '23TEM4 – 2023 Tempranillo',
      },
    ],
  },
  {
    id: 'SUB-23121',
    status: 'At Risk',
    customerName: 'Victoria Moore',
    customer: 'victoria.m@winery.com',
    location: 'Fremantle, WA',
    amount: '$195.00',
    subscriptionTitle: '6 Bottles Every 3 Months',
    discountPercent: 10,
    nextPayment: 'Fri, 22 May 2026',
    startDate: 'Fri, 4 Apr 2025',
    orderCount: 17,
    tags: ['needs-attention', 'loyal'],
    paymentInfo: {
      status: 'failed' as PaymentStatus,
      failureReason: 'Card blocked by bank',
    },
    cardExpiry: {
      expiryMonth: 5,
      expiryYear: 2026,
      // Expires in ~2 months
      last4: '1010',
      brand: 'Visa',
    },
    items: [
      {
        quantity: 2,
        name: '25FID6 – 2025 Fiano',
      },
      {
        quantity: 2,
        name: '24SBB2 – 2024 Sauvignon Blanc',
      },
      {
        quantity: 2,
        name: '25RSG5 – 2025 Rosé Grenache',
      },
    ],
  },
  {
    id: 'SUB-23120',
    status: 'At Risk',
    customerName: 'Daniel Scott',
    customer: 'daniel.s@winery.com',
    location: 'Sunshine Coast, QLD',
    amount: '$285.50',
    subscriptionTitle: '12 Bottles Every 6 Months',
    discountPercent: 10,
    nextPayment: 'Mon, 1 Jun 2026',
    startDate: 'Sat, 5 Apr 2025',
    orderCount: 8,
    tags: ['needs-attention'],
    paymentInfo: {
      status: 'healthy' as PaymentStatus,
    },
    cardExpiry: {
      expiryMonth: 4,
      expiryYear: 2026,
      last4: '2020',
      brand: 'Mastercard',
    },
    items: [
      {
        quantity: 3,
        name: '24SHZ9 – 2024 Shiraz',
      },
      {
        quantity: 3,
        name: '23PNR7 – 2023 Pinot Noir',
      },
      {
        quantity: 3,
        name: '22CSR8 – 2022 Cabernet Sauvignon Reserve',
      },
      {
        quantity: 3,
        name: '25VGN9 – 2025 Viognier',
      },
    ],
  },
]

// Also export as mockSubscriptions for backwards compatibility
export const mockSubscriptions = subscriptions

// Mock event history data with expanded event types
export const mockEvents = [
  {
    id: 1,
    type: 'payment_success',
    date: '30-11-2025',
    title: 'Payment Successful',
    description: 'Charge of $126.50 was successful.',
  },
  {
    id: 2,
    type: 'product_swap',
    date: '28-11-2025',
    title: 'Product Swapped',
    description: 'Customer swapped 2024 Shiraz for 2025 Chardonnay.',
  },
  {
    id: 3,
    type: 'box_change',
    date: '25-11-2025',
    title: 'Subscription Updated',
    description: 'Changed from 6 bottles to 12 bottles per delivery.',
  },
  {
    id: 4,
    type: 'payment_method_updated',
    date: '20-11-2025',
    title: 'Payment Method Updated',
    description: 'Credit card ending in 4242 was updated.',
  },
  {
    id: 5,
    type: 'cycle_skipped',
    date: '15-11-2025',
    title: 'Cycle Skipped',
    description: 'Customer skipped the December delivery cycle.',
  },
  {
    id: 6,
    type: 'email_sent',
    date: '10-11-2025',
    title: 'Email Sent',
    description: 'Upcoming subscription reminder sent to customer.',
  },
  {
    id: 7,
    type: 'note',
    date: '05-11-2025',
    title: 'Admin Note',
    description: 'Customer called to confirm delivery address change.',
    author: 'Sarah Admin',
  },
  {
    id: 8,
    type: 'payment_success',
    date: '30-10-2025',
    title: 'Payment Successful',
    description: 'Charge of $126.50 was successful.',
  },
]

// Mock available products for selection
export const availableProducts = [
  {
    id: 'p1',
    name: '2025 Fiano',
    price: 35.0,
  },
  {
    id: 'p2',
    name: '2024 Shiraz',
    price: 45.0,
  },
  {
    id: 'p3',
    name: '2025 Chardonnay',
    price: 40.0,
  },
  {
    id: 'p4',
    name: '2023 Pinot Noir',
    price: 42.0,
  },
  {
    id: 'p5',
    name: '2024 Sauvignon Blanc',
    price: 38.0,
  },
  {
    id: 'p6',
    name: '2022 Cabernet Sauvignon Reserve',
    price: 55.0,
  },
  {
    id: 'p7',
    name: '2025 Rosé Grenache',
    price: 32.0,
  },
  {
    id: 'p8',
    name: '2023 Tempranillo',
    price: 36.0,
  },
  {
    id: 'p9',
    name: '2024 Pinot Grigio',
    price: 34.0,
  },
  {
    id: 'p10',
    name: '2025 Viognier',
    price: 39.0,
  },
]

// Helper to get status variant
export const getStatusVariant = (
  status: string,
): 'success' | 'warning' | 'info' | 'error' | 'default' => {
  if (status === 'Active') return 'success'
  if (status === 'At Risk') return 'error'
  if (status === 'On Hold') return 'warning'
  if (status === 'Paused') return 'info'
  if (status === 'Cancelled') return 'default'
  return 'default'
}

// Mock pricing logic for display
export const getItemPrice = (itemName: string) => {
  if (itemName.includes('Fiano')) return 35.0
  if (itemName.includes('Shiraz')) return 45.0
  if (itemName.includes('Chardonnay')) return 40.0
  if (itemName.includes('Pinot')) return 42.0
  return 38.0
}

// Event type configuration for filtering
export const eventTypeConfig = {
  all: {
    label: 'All Activity',
    color: '#475569',
  },
  payment_success: {
    label: 'Payments',
    color: '#10B981',
  },
  product_swap: {
    label: 'Product Swaps',
    color: '#0EA5E9',
  },
  box_change: {
    label: 'Box Changes',
    color: '#6F42C1',
  },
  payment_method_updated: {
    label: 'Payment Updates',
    color: '#F59E0B',
  },
  cycle_skipped: {
    label: 'Skipped Cycles',
    color: '#EF4444',
  },
  email_sent: {
    label: 'Emails',
    color: '#3B82F6',
  },
  note: {
    label: 'Notes',
    color: '#64748B',
  },
}

// Helper functions for card expiry
export const calculateDaysUntilExpiry = (
  expiryMonth: number,
  expiryYear: number,
): number => {
  // Card expires at end of the expiry month
  const expiryDate = new Date(expiryYear, expiryMonth, 0) // Last day of expiry month
  const today = new Date()
  today.setHours(0, 0, 0, 0) // Reset time to start of day
  const diffMs = expiryDate.getTime() - today.getTime()
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24))
  return diffDays
}
export const isCardExpiringSoon = (
  expiryMonth: number,
  expiryYear: number,
): boolean => {
  const daysUntilExpiry = calculateDaysUntilExpiry(expiryMonth, expiryYear)
  return daysUntilExpiry >= 0 && daysUntilExpiry <= 60 // Within 60 days
}
export const isCardExpired = (
  expiryMonth: number,
  expiryYear: number,
): boolean => {
  const daysUntilExpiry = calculateDaysUntilExpiry(expiryMonth, expiryYear)
  return daysUntilExpiry < 0
}
export const getCardExpiryUrgency = (
  expiryMonth: number,
  expiryYear: number,
): 'expired' | 'urgent' | 'warning' | 'notice' | 'ok' => {
  const daysUntilExpiry = calculateDaysUntilExpiry(expiryMonth, expiryYear)
  if (daysUntilExpiry < 0) return 'expired'
  if (daysUntilExpiry <= 14) return 'urgent' // ≤14 days
  if (daysUntilExpiry <= 30) return 'warning' // 15-30 days
  if (daysUntilExpiry <= 60) return 'notice' // 31-60 days
  return 'ok'
}
export const formatCardExpiry = (
  expiryMonth: number,
  expiryYear: number,
): string => {
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ]
  return `${monthNames[expiryMonth - 1]} ${expiryYear}`
}

// Global available tags for the system
export const availableTags = [
  {
    id: 'premium',
    label: 'Premium',
    color: 'purple' as const,
  },
  {
    id: 'vip',
    label: 'VIP',
    color: 'purple' as const,
  },
  {
    id: 'loyal',
    label: 'Loyal',
    color: 'blue' as const,
  },
  {
    id: 'new-customer',
    label: 'New Customer',
    color: 'green' as const,
  },
  {
    id: 'needs-attention',
    label: 'Needs Attention',
    color: 'orange' as const,
  },
  {
    id: 'high-value',
    label: 'High Value',
    color: 'purple' as const,
  },
  {
    id: 'seasonal',
    label: 'Seasonal',
    color: 'blue' as const,
  },
  {
    id: 'corporate',
    label: 'Corporate',
    color: 'gray' as const,
  },
]

// Mock payment retry history
export const mockPaymentRetryHistory: Record<
  string,
  Array<{
    id: string
    attemptNumber: number
    date: string
    time: string
    status: 'success' | 'failed' | 'pending'
    reason?: string
    amount: string
  }>
> = {
  'SUB-23141': [
    // Michael Chen - In Retry (2/4)
    {
      id: 'retry-3',
      attemptNumber: 3,
      date: '28 Jan, 2026',
      time: '9:00 AM',
      status: 'pending',
      amount: '$189.50',
    },
    {
      id: 'retry-2',
      attemptNumber: 2,
      date: '25 Jan, 2026',
      time: '9:00 AM',
      status: 'failed',
      reason: 'Insufficient funds',
      amount: '$189.50',
    },
    {
      id: 'retry-1',
      attemptNumber: 1,
      date: '22 Jan, 2026',
      time: '9:00 AM',
      status: 'failed',
      reason: 'Insufficient funds',
      amount: '$189.50',
    },
    {
      id: 'initial',
      attemptNumber: 0,
      date: '19 Jan, 2026',
      time: '2:30 PM',
      status: 'failed',
      reason: 'Card declined',
      amount: '$189.50',
    },
  ],
  'SUB-23138': [
    // James Anderson - Final Retry (4/4)
    {
      id: 'retry-4',
      attemptNumber: 4,
      date: '28 Jan, 2026',
      time: '2:00 PM',
      status: 'pending',
      amount: '$315.00',
    },
    {
      id: 'retry-3',
      attemptNumber: 3,
      date: '25 Jan, 2026',
      time: '2:00 PM',
      status: 'failed',
      reason: 'Bank declined',
      amount: '$315.00',
    },
    {
      id: 'retry-2',
      attemptNumber: 2,
      date: '22 Jan, 2026',
      time: '2:00 PM',
      status: 'failed',
      reason: 'Bank declined',
      amount: '$315.00',
    },
    {
      id: 'retry-1',
      attemptNumber: 1,
      date: '19 Jan, 2026',
      time: '2:00 PM',
      status: 'failed',
      reason: 'Card declined',
      amount: '$315.00',
    },
    {
      id: 'initial',
      attemptNumber: 0,
      date: '16 Jan, 2026',
      time: '2:00 PM',
      status: 'failed',
      reason: 'Insufficient funds',
      amount: '$315.00',
    },
  ],
  'SUB-23137': [
    // Olivia Martinez - On Hold
    {
      id: 'retry-4',
      attemptNumber: 4,
      date: '25 Jan, 2026',
      time: '10:00 AM',
      status: 'failed',
      reason: 'Max retries exhausted',
      amount: '$98.75',
    },
    {
      id: 'retry-3',
      attemptNumber: 3,
      date: '22 Jan, 2026',
      time: '10:00 AM',
      status: 'failed',
      reason: 'Card declined',
      amount: '$98.75',
    },
    {
      id: 'retry-2',
      attemptNumber: 2,
      date: '19 Jan, 2026',
      time: '10:00 AM',
      status: 'failed',
      reason: 'Insufficient funds',
      amount: '$98.75',
    },
    {
      id: 'retry-1',
      attemptNumber: 1,
      date: '16 Jan, 2026',
      time: '10:00 AM',
      status: 'failed',
      reason: 'Insufficient funds',
      amount: '$98.75',
    },
    {
      id: 'initial',
      attemptNumber: 0,
      date: '13 Jan, 2026',
      time: '10:00 AM',
      status: 'failed',
      reason: 'Card declined',
      amount: '$98.75',
    },
  ],
  'SUB-23136': [
    // William Taylor - Failed
    {
      id: 'initial',
      attemptNumber: 0,
      date: '20 Jan, 2026',
      time: '3:00 PM',
      status: 'failed',
      reason: 'Payment method revoked',
      amount: '$275.50',
    },
  ],
  'SUB-23133': [
    // Isabella Garcia - In Retry (1/4)
    {
      id: 'retry-2',
      attemptNumber: 2,
      date: '30 Jan, 2026',
      time: '11:00 AM',
      status: 'pending',
      amount: '$156.25',
    },
    {
      id: 'retry-1',
      attemptNumber: 1,
      date: '27 Jan, 2026',
      time: '11:00 AM',
      status: 'failed',
      reason: 'Card expired',
      amount: '$156.25',
    },
    {
      id: 'initial',
      attemptNumber: 0,
      date: '24 Jan, 2026',
      time: '11:00 AM',
      status: 'failed',
      reason: 'Card expired',
      amount: '$156.25',
    },
  ],
  'SUB-23130': [
    // Ethan White - In Retry (3/4) - Overdue
    {
      id: 'retry-3',
      attemptNumber: 3,
      date: '26 Jan, 2026',
      time: '1:00 PM',
      status: 'failed',
      reason: 'Bank declined',
      amount: '$298.75',
    },
    {
      id: 'retry-2',
      attemptNumber: 2,
      date: '23 Jan, 2026',
      time: '1:00 PM',
      status: 'failed',
      reason: 'Insufficient funds',
      amount: '$298.75',
    },
    {
      id: 'retry-1',
      attemptNumber: 1,
      date: '20 Jan, 2026',
      time: '1:00 PM',
      status: 'failed',
      reason: 'Insufficient funds',
      amount: '$298.75',
    },
    {
      id: 'initial',
      attemptNumber: 0,
      date: '17 Jan, 2026',
      time: '1:00 PM',
      status: 'failed',
      reason: 'Card declined',
      amount: '$298.75',
    },
  ],
  'SUB-23127': [
    // Charlotte Thompson - On Hold
    {
      id: 'retry-4',
      attemptNumber: 4,
      date: '24 Jan, 2026',
      time: '4:00 PM',
      status: 'failed',
      reason: 'Awaiting customer action',
      amount: '$335.25',
    },
    {
      id: 'retry-3',
      attemptNumber: 3,
      date: '21 Jan, 2026',
      time: '4:00 PM',
      status: 'failed',
      reason: 'Card declined',
      amount: '$335.25',
    },
    {
      id: 'retry-2',
      attemptNumber: 2,
      date: '18 Jan, 2026',
      time: '4:00 PM',
      status: 'failed',
      reason: 'Insufficient funds',
      amount: '$335.25',
    },
    {
      id: 'retry-1',
      attemptNumber: 1,
      date: '15 Jan, 2026',
      time: '4:00 PM',
      status: 'failed',
      reason: 'Insufficient funds',
      amount: '$335.25',
    },
    {
      id: 'initial',
      attemptNumber: 0,
      date: '12 Jan, 2026',
      time: '4:00 PM',
      status: 'failed',
      reason: 'Card declined',
      amount: '$335.25',
    },
  ],
  'SUB-23121': [
    // Victoria Moore - Failed
    {
      id: 'initial',
      attemptNumber: 0,
      date: '23 Jan, 2026',
      time: '5:00 PM',
      status: 'failed',
      reason: 'Card blocked by bank',
      amount: '$195.00',
    },
  ],
}
